import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataGrid, {
  Column,
  Paging,
  Pager,
  FilterRow,
  Selection,
  SearchPanel,
  Toolbar,
  Item,
} from 'devextreme-react/data-grid';
import { ContextMenu } from 'devextreme-react/context-menu';
import { LoadPanel } from 'devextreme-react/load-panel';
import api from '../../services/api';
import DocumentSelector from "../../components/DocumentSelector";
import { exportToExcel } from "../../utils/exportUtils";
import 'devextreme/dist/css/dx.material.blue.light.compact.css';
import EditIcon from '@mui/icons-material/Edit';

const AgentApplicants = () => {
  // State management
  const [rawData, setRawData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [focusedRowKey, setFocusedRowKey] = useState(null);
  const [isDocumentSelectorOpen, setIsDocumentSelectorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Initialize as true to show loading initially
  const [error, setError] = useState(null);

  // Refs
  const gridWrapperRef = useRef(null);
  const contextMenuRef = useRef(null);
  const dataGridRef = useRef(null);
  const navigate = useNavigate();

  // Helper functions
  const calculateAge = useCallback((dateString) => {
    if (!dateString) return null;
    try {
      const birthDate = new Date(dateString);
      if (isNaN(birthDate.getTime())) return null;
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (err) {
      console.error('Error calculating age:', err);
      return null;
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
    } catch (err) {
      console.error('Error formatting date:', err);
      return '';
    }
  }, []);

  const transformData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => ({
      ID: item.id || item.ID,
      applicationDate: formatDate(item.applicationDate || item.createdAt),
      applicationNo: item.applicationNo || item.applicationNumber || '',
      fullName: item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim(),
      passportNo: item.passportNo || item.passportNumber || '',
      age: calculateAge(item.dateOfBirth),
      religion: item.religion || '',
      experienceInAbroad: item.experienceInAbroad || item.experience || '',
      workingCountry: item.workingCountry || item.country || '',
      sponsorName: item.sponsorName || item.sponsor || '',
      sponsorID: item.sponsorID || item.sponsorId || '',
      visaNo: item.visaNo || item.visaNumber || '',
      cocCenter: item.cocCenter || item.coc || '',
      medicalOnlineStatus: item.medicalOnlineStatus || item.medicalStatus || '',
      medicalDays: item.medicalDays || item.medicalDaysCount || '',
      status: item.status || item.currentStatus || '',
      statusDate: formatDate(item.statusDate || item.statusUpdatedAt),
      bioEntryDate: formatDate(item.bioEntryDate || item.lmisDate),
      laborID: item.laborID || item.laborId || '',
      signedDays: item.signedDays || item.daysSigned || '',
      inMusaned: item.inMusaned || item.musanedStatus || '',
      createdBy: item.createdBy || item.createdByName || '',
      _raw: item
    }));
  }, [calculateAge, formatDate]);

  // Memoized data source
  const dataSource = useMemo(() => transformData(rawData), [rawData, transformData]);

  // Get the currently selected row data
  const selectedRowData = useMemo(() => {
    if (!focusedRowKey) return null;
    return dataSource.find(item => item.ID === focusedRowKey);
  }, [focusedRowKey, dataSource]);

  // Applicant Data fetching 
  const fetchApplicants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const endpoint = "/Applicants/agentapplicants";

    
    try {
      const response = await api.get(endpoint, { timeout: 60000 });

      if (response?.data) {
        setRawData(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('API error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  // Event handlers
  const handleFocusedRowChanged = useCallback((e) => {
    if (e.row) {
      setFocusedRowKey(e.row.key);
    }
  }, []);

  const handleSelectionChange = useCallback((e) => {
    setSelectedIds(e.selectedRowKeys);
    // Keep focus on the last selected item
    if (e.selectedRowKeys.length > 0) {
      setFocusedRowKey(e.selectedRowKeys[e.selectedRowKeys.length - 1]);
    } else {
      setFocusedRowKey(null);
    }
  }, []);

  const handleContextMenuPreparing = useCallback((e) => {
    const shouldShowCustomMenu = (
      e.row?.rowType === "data" && 
      e.row?.data && 
      gridWrapperRef.current?.contains(e.event.target)
    );
  
    if (shouldShowCustomMenu) {
      e.event.preventDefault();
      setFocusedRowKey(e.row.key);
      
      // Get the click position relative to the document
      const clickX = e.event.pageX;
      const clickY = e.event.pageY;
      
      // Show the context menu at click position
      setTimeout(() => {
        contextMenuRef.current?.instance.option({
          position: {
            of: document.documentElement, // Use document as reference
            my: 'top left',
            at: 'top left',
            offset: `${clickX} ${clickY}`
          }
        });
        contextMenuRef.current?.instance.show();
      }, 10);
    }
}, []);

  const handleExportSelected = useCallback(async (ids) => {
    try {
      const apiEndpoint = "/applicants/export-selected";
      const filename = `Applicants_${new Date().toISOString().slice(0, 10)}.xlsx`;
      await exportToExcel(ids, apiEndpoint, filename);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Export failed: ' + (err.message || 'Unknown error'));
    }
  }, []);

  const handleAction = useCallback((type) => {
    const idsToUse = selectedIds.length > 0 ? selectedIds : 
                   (selectedRowData ? [selectedRowData.ID] : []);
                   
    if (idsToUse.length === 0) return;
    
    switch(type) {
      case 'cv':
          navigate("/reports/print_cv", { state: { selectedIds: idsToUse } });
          break;
      case 'documents':
          setIsDocumentSelectorOpen(true);
          break;
      default:
          break;      
    }
  }, [selectedIds, selectedRowData, navigate]);

  const handleContextMenuClick = useCallback((e) => {
    if (!selectedRowData) return;

    const idsToUse = selectedIds.length > 0 ? selectedIds : [selectedRowData.ID];

    switch (e.itemData.text) {
      case 'Print CV':
        navigate("/reports/print_cv", { state: { selectedIds: idsToUse } });
        break;
      case 'Print Selected Documents':
        setIsDocumentSelectorOpen(true);
        break;
      default:
        break;
    }
  }, [selectedIds, selectedRowData, navigate]);

  const handleDocumentSelection = useCallback((selectedItems) => {
    setIsDocumentSelectorOpen(false);
    const idsToUse = selectedIds.length > 0 ? selectedIds : [selectedRowData?.ID].filter(Boolean);
    navigate("/reports/print_documents", { 
      state: { 
        selectedIds: idsToUse, 
        selectedItems 
      } 
    });
  }, [selectedIds, selectedRowData, navigate]);

  // Column configuration
  const columns = useMemo(() => [
    { 
      caption: "...",
      width: 43,
      //cellRender: (data) => <Link to={`/applicants/edit/${data.data.ID}`}>Edit...</Link>
      cellRender: (data) => (<Link to={`/applicants/edit/${data.data.ID}`}> <EditIcon fontSize="small" /> </Link>)
    },
    { dataField: 'applicationDate', caption: 'Date' },
    { dataField: 'applicationNo', caption: 'Application#', dataType: "string" },
    { dataField: 'fullName', caption: 'Full Name', dataType: "string"},
    { dataField: 'passportNo', caption: 'Passport#', dataType: "string" },
    { dataField: 'age', caption: 'Age' },
    { dataField: 'experienceInAbroad', caption: 'Exp' },
    { dataField: 'workingCountry', caption: 'Works In', dataType: "string" },
    { dataField: 'sponsorName', caption: 'Sponsor', dataType: "string" },
    { dataField: 'sponsorID', caption: 'Sponsor ID', dataType: "string" },
    { dataField: 'visaNo', caption: 'Visa No', dataType: "string" },
    { dataField: 'cocCenter', caption: 'COC', dataType: "string" },
    { dataField: 'medicalOnlineStatus', caption: 'MED' },
    { dataField: 'medicalDays', caption: 'M.Days', dataType: "string" },
    { dataField: 'status', caption: 'Status', dataType: "string" },
    { dataField: 'statusDate', caption: 'Status Date' },
    { dataField: 'bioEntryDate', caption: 'LMIS' },
    { dataField: 'laborID', caption: 'Labor ID', dataType: "string" },
    { dataField: 'signedDays', caption: 'Days' },
    { dataField: 'createdBy', caption: 'User', dataType: "string" },
    { dataField: 'religion', caption: 'Religion', dataType: "string" },
    { dataField: 'inMusaned', caption: 'MUS' },
  ], []);

  // Context menu items
  const contextMenuItems = useMemo(() => [
    { text: 'Print CV', icon: 'print' },
    { text: 'Print Selected Documents', icon: 'print' }
  ], []);

  return (
    <div className="w-100 h-100 bg-white rounded p-1 position-relative" ref={gridWrapperRef}>
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: gridWrapperRef.current || window }}
        visible={isLoading}
        showIndicator={true}
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
      />

      {!isLoading && !error && (
        <DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr="ID"
          showBorders
          rowAlternationEnabled
          allowColumnResizing
          columnAutoWidth
          wordWrapEnabled
          focusedRowEnabled
          showColumnLines
          height="100%"
          width="100%"
          focusedRowKey={focusedRowKey}
          selectedRowKeys={selectedIds}
          onSelectionChanged={handleSelectionChange}
          onContextMenuPreparing={handleContextMenuPreparing}
          onFocusedRowChanged={handleFocusedRowChanged}
          repaintChangesOnly
        >
          <Toolbar>
            <Item location="before">
              <div className="d-flex align-items-center es-label-title">
                <strong>APPLICANTS</strong> 
                {isLoading ? (
                  <span className="text-primary ms-1">Loading...</span>
                ) : error ? (
                  <span className="text-danger ms-1">Error: {error}</span>
                ) : (
                  <>
                    <span className="es-label-title ms-1">[{dataSource.length}]</span>
                    {selectedIds.length > 0 && (
                      <span className="text-primary ms-2">
                        Selected: {selectedIds.length}
                      </span>
                    )}
                  </>
                )}
              </div>
            </Item>

            <Item 
              location="before"
              widget="dxDropDownButton"
              locateInMenu="auto"
              options={{
                text: 'Actions',
                icon: 'menu',
                items: [
                  { text: 'Print CV', onClick: () => handleAction('cv'), disabled:!selectedRowData },
                  { text: 'Print Documents', onClick: () => handleAction('documents'), disabled:!selectedRowData }
                ],
                dropDownOptions: { width: 220 },
                stylingMode: 'text'
              }}
            />

            <Item name="searchPanel" locateInMenu="never" />

            <Item location="after" locateInMenu="never">
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchApplicants}
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </Item>
          </Toolbar>

          <SearchPanel visible highlightCaseSensitive />
          <FilterRow visible />
          <Selection 
            mode="multiple" 
            showCheckBoxesMode="always"
            selectAllMode="page"
          />
          <Paging defaultPageSize={50} />
          <Pager 
            showPageSizeSelector 
            allowedPageSizes={[15, 25, 50]} 
            showInfo 
          />

          {columns.map((column) => (
            <Column
              key={column.dataField || column.caption}
              {...column}
              allowFiltering
              filterOperations={column.dataType === "string" ? ["startswith", "contains", "notcontains", "endswith", "=", "<>"] : column.filterOperations}
            />
          ))}
        </DataGrid>
      )}

      {error && (
        <div className="alert alert-danger m-3">
          <strong>Error loading data:</strong> {error}
          <button className="btn btn-sm btn-danger ms-3" onClick={fetchApplicants}>
            Retry
          </button>
        </div>
      )}

      <ContextMenu
        ref={contextMenuRef}
        onItemClick={handleContextMenuClick}
        items={contextMenuItems}
        target={gridWrapperRef.current}
        onShowing={(e) => {
          if (!focusedRowKey) {
            e.cancel = true;
          }
        }}
        showEvent={null}
        hideOnOutsideClick={true}
        cssClass="applicant-context-menu"
        //position={{
        //  my: 'top left',
        //  at: 'top left',
        //  collision: 'fit flip'
        //}}
      />
      
      <DocumentSelector
        isOpen={isDocumentSelectorOpen}
        onClose={() => setIsDocumentSelectorOpen(false)}
        onSubmit={handleDocumentSelection}
      />
    </div>
  );
};

export default AgentApplicants;